import type { AdsDailyMetric } from '@/modules/ads/types'

export type MetricFormat = 'currency' | 'number' | 'percent' | 'ratio'
export type MetricAggregation = 'sum' | 'average'

export interface AdsMetricDefinition {
  key: keyof Omit<AdsDailyMetric, 'clientId' | 'date'>
  label: string
  format: MetricFormat
  aggregation: MetricAggregation
  /** True when the underlying field is not yet ingested and is surfaced as zero/placeholder. */
  isDemo?: boolean
}

export const ADS_METRIC_CATALOG: AdsMetricDefinition[] = [
  { key: 'adSpend', label: 'Ad Spend (All)', format: 'currency', aggregation: 'sum' },
  { key: 'adDrivenGmv', label: 'Ad-Driven GMV (All)', format: 'currency', aggregation: 'sum' },
  { key: 'roas', label: 'ROAS (All)', format: 'ratio', aggregation: 'average' },
  { key: 'gmvMaxSpend', label: 'GMV Max Spend', format: 'currency', aggregation: 'sum' },
  { key: 'gmvMaxRevenue', label: 'GMV Max Revenue', format: 'currency', aggregation: 'sum' },
  { key: 'gmvMaxRoas', label: 'GMV Max ROI', format: 'ratio', aggregation: 'average' },
  { key: 'gmvMaxImpressions', label: 'GMV Max Impressions', format: 'number', aggregation: 'sum' },
  { key: 'gmvMaxCpm', label: 'GMV Max CPM', format: 'currency', aggregation: 'average' },
  { key: 'standardSpend', label: 'Standard Ads Spend', format: 'currency', aggregation: 'sum' },
  { key: 'standardRevenue', label: 'Standard Ads Revenue', format: 'currency', aggregation: 'sum' },
  { key: 'standardRoas', label: 'Standard Ads ROAS', format: 'ratio', aggregation: 'average' },
  { key: 'adsPctGmv', label: 'Ads % of GMV', format: 'percent', aggregation: 'average' },
]

export const DEFAULT_ADS_KPI_METRICS: Array<AdsMetricDefinition['key']> = [
  'gmvMaxSpend',
  'gmvMaxRoas',
  'standardSpend',
  'standardRoas',
  'adSpend',
]

export function aggregateAds(
  data: AdsDailyMetric[],
  definition: AdsMetricDefinition,
): number {
  if (data.length === 0) return 0
  // Special-case ROAS — averaging a daily ratio is misleading; recompute from
  // totals so the KPI matches what you'd get summing spend and revenue.
  // Same reasoning for ads-as-%-of-GMV: recompute from totals.
  if (definition.key === 'adsPctGmv') {
    const totalSpend = data.reduce((acc, d) => acc + (d.adSpend ?? 0), 0)
    const totalGmv = data.reduce((acc, d) => acc + (d.adDrivenGmv ?? 0), 0)
    return totalGmv > 0 ? totalSpend / totalGmv : 0
  }
  if (definition.key === 'roas') {
    const totalSpend = data.reduce((acc, d) => acc + (d.adSpend ?? 0), 0)
    const totalGmv = data.reduce((acc, d) => acc + (d.adDrivenGmv ?? 0), 0)
    return totalSpend > 0 ? totalGmv / totalSpend : 0
  }
  if (definition.key === 'gmvMaxRoas') {
    const totalSpend = data.reduce((acc, d) => acc + (d.gmvMaxSpend ?? 0), 0)
    const totalRevenue = data.reduce((acc, d) => acc + (d.gmvMaxRevenue ?? 0), 0)
    return totalSpend > 0 ? totalRevenue / totalSpend : 0
  }
  if (definition.key === 'standardRoas') {
    const totalSpend = data.reduce((acc, d) => acc + (d.standardSpend ?? 0), 0)
    const totalRevenue = data.reduce((acc, d) => acc + (d.standardRevenue ?? 0), 0)
    return totalSpend > 0 ? totalRevenue / totalSpend : 0
  }
  // CPM: weight each day's CPM by its impressions so the headline equals
  // total cost / total impressions × 1000 (a plain mean over-weights quiet days).
  if (definition.key === 'gmvMaxCpm') {
    const totalImpressions = data.reduce((acc, d) => acc + (d.gmvMaxImpressions ?? 0), 0)
    const totalCostTimes1000 = data.reduce(
      (acc, d) => acc + (d.gmvMaxCpm ?? 0) * (d.gmvMaxImpressions ?? 0),
      0,
    )
    return totalImpressions > 0 ? totalCostTimes1000 / totalImpressions : 0
  }
  const values = data.map((d) => d[definition.key] as number)
  const total = values.reduce((acc, v) => acc + (v ?? 0), 0)
  return definition.aggregation === 'average' ? total / data.length : total
}

export function getAdsMetricDefinition(
  key: AdsMetricDefinition['key'],
): AdsMetricDefinition | undefined {
  return ADS_METRIC_CATALOG.find((m) => m.key === key)
}
